import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DayPicker } from 'react-day-picker'

import { cn } from './cn'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3 bg-white', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium text-fg',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors border border-line bg-surface hover:bg-surface-hover disabled:pointer-events-none disabled:opacity-50',
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell: 'text-fg-secondary rounded-md w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-surface-hover/50 [&:has([aria-selected])]:bg-surface-hover first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        day: cn(
          'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-surface-hover disabled:pointer-events-none disabled:opacity-50',
          'h-9 w-9 p-0 font-normal text-fg aria-selected:opacity-100 hover:bg-sage-lt hover:border hover:border-sage rounded-sm'
        ),
        day_range_end: 'day-range-end',
        day_selected:
          'bg-brand-active text-white border border-brand rounded-sm hover:bg-brand-active hover:text-white hover:border-brand focus:bg-brand-active focus:text-white',
        day_today: 'bg-surface-alt text-fg font-semibold',
        day_outside:
          'day-outside text-fg-muted opacity-50 aria-selected:bg-surface-hover/50 aria-selected:text-fg-secondary aria-selected:opacity-30',
        day_disabled: 'text-fg-muted opacity-50',
        day_range_middle:
          'aria-selected:bg-surface-hover aria-selected:text-fg',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
